import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileSpreadsheet, Download, Copy, ShieldAlert, AlertTriangle, Loader2,
  CheckCircle2, ArrowLeft, Calendar, FileText, Layers,
} from 'lucide-react';

import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';

import PeriodPicker, { defaultDashboardRange } from '../../components/mec/PeriodPicker';

import {
  listRouting, getClose, getJournalEntry, downloadJournalEntry,
} from '../../api/mec';
import { formatMoney, formatDate, formatRelative } from '../../utils/formatters';

// MEC Journal Entry generator (View 4 per MEC_REQUIREMENTS.md §7).
//
// Per D-019 the JE pulls from a finalized mec_close_run snapshot (not live
// data). For v1 we surface single-shop output; "all shops" aggregation is
// v1.1. Backend agent may still be wiring the endpoints — handle 404 with a
// "not yet deployed" message rather than crashing.

export default function JournalEntry() {
  const navigate = useNavigate();

  const [routing, setRouting] = useState([]);
  const [routingLoading, setRoutingLoading] = useState(true);
  const [routingError, setRoutingError] = useState(null);

  const defaultRange = useMemo(() => defaultDashboardRange(), []);
  const [periodStart, setPeriodStart] = useState(defaultRange.start);
  const [periodEnd, setPeriodEnd] = useState(defaultRange.end);

  const [selectedShopId, setSelectedShopId] = useState(null);

  // Close snapshot for the chosen shop+period (gates whether JE is available)
  const [closeResult, setCloseResult] = useState(null);
  const [closeLoading, setCloseLoading] = useState(false);
  const [closeError, setCloseError] = useState(null);

  // JE format toggle
  const [format, setFormat] = useState('consolidated'); // 'consolidated' | 'class-split'

  // Generated JE payload
  const [je, setJe] = useState(null);
  const [jeLoading, setJeLoading] = useState(false);
  const [jeError, setJeError] = useState(null);
  const [endpointMissing, setEndpointMissing] = useState(false);

  const [copyState, setCopyState] = useState(null); // 'copied' | null

  // Load routing → pick first shop
  useEffect(() => {
    let alive = true;
    setRoutingLoading(true);
    setRoutingError(null);
    listRouting()
      .then((data) => {
        if (!alive) return;
        const shops = (data?.shops || []).filter((s) => s.shop_id !== 7515);
        setRouting(shops);
        if (shops.length && !selectedShopId) {
          setSelectedShopId(shops[0].shop_id);
        }
      })
      .catch((err) => {
        if (!alive) return;
        setRoutingError(err?.message || 'Failed to load shops');
      })
      .finally(() => alive && setRoutingLoading(false));
    return () => { alive = false; };
  }, []);

  // Load close snapshot when shop or period changes
  useEffect(() => {
    if (!selectedShopId || !periodStart || !periodEnd) {
      setCloseResult(null);
      return;
    }
    let alive = true;
    setCloseLoading(true);
    setCloseError(null);
    setCloseResult(null);
    getClose(selectedShopId, periodStart, periodEnd)
      .then((data) => alive && setCloseResult(data))
      .catch((err) => {
        if (!alive) return;
        if (/404|not found/i.test(err?.message || '')) {
          setCloseResult(null);
        } else {
          setCloseError(err?.message || 'Failed to load close run');
        }
      })
      .finally(() => alive && setCloseLoading(false));
    return () => { alive = false; };
  }, [selectedShopId, periodStart, periodEnd]);

  // Load JE when we have a close_run_id
  const loadJe = useCallback(() => {
    if (!closeResult?.close_run_id) {
      setJe(null);
      return;
    }
    let alive = true;
    setJeLoading(true);
    setJeError(null);
    setEndpointMissing(false);
    setJe(null);
    getJournalEntry(closeResult.close_run_id, format)
      .then((data) => alive && setJe(data))
      .catch((err) => {
        if (!alive) return;
        if (/404|not found|not yet/i.test(err?.message || '')) {
          setEndpointMissing(true);
        } else {
          setJeError(err?.message || 'Failed to generate journal entry');
        }
      })
      .finally(() => alive && setJeLoading(false));
    return () => { alive = false; };
  }, [closeResult?.close_run_id, format]);

  useEffect(() => loadJe(), [loadJe]);

  const selectedShop = useMemo(
    () => routing.find((s) => s.shop_id === selectedShopId) || null,
    [routing, selectedShopId]
  );

  // Build minimal JE preview from local close data when endpoint is missing.
  // This is a degraded fallback so the user still sees structure.
  const localJePreview = useMemo(() => {
    if (!endpointMissing || !closeResult) return null;
    const parts = closeResult;
    const a_used = (parts.a_used_cents || 0) / 100;
    const a_pending_return = (parts.a_pending_return_cents || 0) / 100;
    const delta_wip = (parts.delta_wip_cents
      ?? ((parts.b_total_cents ?? ((parts.b_received_cents || 0) + (parts.b_pending_return_cents || 0))) - (parts.b0_cents || 0))) / 100;
    const variance = (parts.variance_cents || 0) / 100;
    const p = (parts.p_cents || 0) / 100;
    return {
      reverse_date: '(1st of next month)',
      shop_name: selectedShop?.shop_name,
      lines: [
        {
          account: 'Parts COGS (Matched)',
          debit: a_used + a_pending_return,
          credit: 0,
          description: `Tekmetric matched usage including ${formatMoney(a_pending_return)} pending returns`,
        },
        {
          account: 'WIP — Parts Inventory',
          debit: Math.max(0, delta_wip),
          credit: Math.max(0, -delta_wip),
          description: 'Period-end ΔWIP',
        },
        {
          account: 'Parts Purchases (offset)',
          debit: 0,
          credit: p,
          description: 'QBO Parts Purchases for period',
        },
        ...(variance !== 0 ? [{
          account: variance > 0 ? 'Parts Variance (Leakage)' : 'Parts Variance (Recovery)',
          debit: variance > 0 ? variance : 0,
          credit: variance > 0 ? 0 : -variance,
          description: 'Unreconciled — investigate',
        }] : []),
      ],
    };
  }, [endpointMissing, closeResult, selectedShop]);

  const displayJe = je || localJePreview;
  const isFromBackend = !!je;

  // Backend payload uses `debit_cents`/`credit_cents` (integers, cents).
  // Local fallback preview emits `debit`/`credit` (dollars). Read either.
  const lineDebitDollars = (l) =>
    l.debit_cents != null ? l.debit_cents / 100 : Number(l.debit) || 0;
  const lineCreditDollars = (l) =>
    l.credit_cents != null ? l.credit_cents / 100 : Number(l.credit) || 0;

  const totals = useMemo(() => {
    if (!displayJe?.lines) return { debit: 0, credit: 0, balanced: true };
    let d = 0;
    let c = 0;
    for (const line of displayJe.lines) {
      d += lineDebitDollars(line);
      c += lineCreditDollars(line);
    }
    return { debit: d, credit: c, balanced: Math.abs(d - c) < 0.005 };
  }, [displayJe]);

  // Backend returns `text_for_paste`; local fallback uses `text`. Read either.
  const jeText = displayJe?.text_for_paste || displayJe?.text || '';
  const jeReverseDate = displayJe?.auto_reverse_date || displayJe?.reverse_date || null;

  const onCopy = () => {
    if (!jeText) return;
    navigator.clipboard.writeText(jeText).then(() => {
      setCopyState('copied');
      setTimeout(() => setCopyState(null), 2000);
    });
  };

  const onDownload = async (kind) => {
    if (!closeResult?.close_run_id) return;
    try {
      const blobUrl = await downloadJournalEntry(closeResult.close_run_id, kind);
      const a = document.createElement('a');
      a.href = blobUrl;
      const ext = kind === 'csv' ? 'csv' : 'txt';
      a.download = `mec-je-${selectedShopId}-${periodStart}-${periodEnd}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      setJeError(err?.message || `Download failed`);
    }
  };

  return (
    <div>
      <PageHeader
        title="Journal Entry"
        description="Generate a posting-ready JE from a finalized close run. Output as formatted text + CSV for QuickBooks paste."
        breadcrumbs={[
          { label: 'Month-End Close', href: '/mec' },
          { label: 'Journal Entry' },
        ]}
        actions={
          <Button
            variant="ghost"
            size="md"
            leftIcon={<ArrowLeft size={15} />}
            onClick={() => navigate('/mec')}
          >
            Dashboard
          </Button>
        }
      />

      {/* Read-only QBO banner */}
      <div className="mb-5 flex items-start gap-3 p-4 rounded-[var(--radius-md)] bg-[var(--color-action-50)] border border-[var(--color-action-100)]">
        <ShieldAlert size={16} className="shrink-0 mt-0.5 text-[var(--color-action-600)]" />
        <div className="text-[13px] text-[var(--color-action-700)] leading-relaxed">
          <strong className="font-semibold">For paste into QuickBooks only.</strong>{' '}
          The Month-End Close module never writes to QBO. This JE is generated for you to review and post manually.
        </div>
      </div>

      {/* Shop + period selectors */}
      <Card className="mb-5">
        <div className="flex flex-col gap-4">
          <PeriodPicker start={periodStart} end={periodEnd} onChange={(s, e) => {
            setPeriodStart(s);
            setPeriodEnd(e);
          }} />
          <div className="h-px bg-[var(--color-warm-100)] -mx-5" />
          <div className="flex items-center gap-3 flex-wrap">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[1px] font-bold text-[var(--color-warm-500)]">
              <FileSpreadsheet size={13} />
              Shop
            </div>
            <div className="inline-flex items-center gap-1.5 flex-wrap">
              {routing.map((s) => {
                const active = selectedShopId === s.shop_id;
                return (
                  <button
                    key={s.shop_id}
                    type="button"
                    onClick={() => setSelectedShopId(s.shop_id)}
                    className={`
                      h-7 px-2.5 text-[12px] font-semibold rounded-[var(--radius-full)] border transition-colors
                      ${active
                        ? 'bg-[var(--color-warm-900)] text-[var(--color-cream-50)] border-[var(--color-warm-900)]'
                        : 'bg-[var(--color-cream-50)] text-[var(--color-warm-600)] border-[var(--color-warm-200)] hover:border-[var(--color-warm-300)]'}
                    `}
                  >
                    {s.shop_name || `Shop ${s.shop_id}`}
                  </button>
                );
              })}
            </div>
            <div className="text-[11px] text-[var(--color-warm-500)] italic ml-2">
              Aggregate "all shops" coming in v1.1.
            </div>
          </div>
        </div>
      </Card>

      {routingError && (
        <Card className="mb-5">
          <div className="flex items-start gap-3 py-1 text-[var(--color-danger-700)]">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div className="text-[14px]">{routingError}</div>
          </div>
        </Card>
      )}

      {closeError && (
        <Card className="mb-5">
          <div className="flex items-start gap-3 py-1 text-[var(--color-danger-700)]">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div className="text-[14px]">{closeError}</div>
          </div>
        </Card>
      )}

      {/* No close run state */}
      {!closeLoading && !closeResult && !closeError && (
        <Card>
          <div className="flex flex-col items-center text-center py-10">
            <FileSpreadsheet size={28} className="text-[var(--color-warm-400)] mb-3" />
            <div className="text-[15px] font-semibold text-[var(--color-warm-900)] mb-1">
              No close run for this shop + period.
            </div>
            <div className="text-[12px] text-[var(--color-warm-500)] max-w-md mb-5">
              The JE generator reads from a finalized close snapshot. Run a close for {selectedShop?.shop_name || 'this shop'} on {formatDate(periodStart)} – {formatDate(periodEnd)} to populate.
            </div>
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate('/mec')}
            >
              Run close on Dashboard
            </Button>
          </div>
        </Card>
      )}

      {closeLoading && (
        <Card>
          <div className="flex items-center gap-2 justify-center py-12 text-[12px] text-[var(--color-warm-500)]">
            <Loader2 size={14} className="animate-spin" />
            Loading close snapshot…
          </div>
        </Card>
      )}

      {closeResult && (
        <>
          {/* Format toggle + actions */}
          <Card className="mb-4 !p-3">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="inline-flex items-center bg-[var(--color-cream-100)] rounded-[var(--radius-full)] p-1 gap-0.5 border border-[var(--color-warm-100)]">
                  {[
                    { key: 'consolidated', label: 'Consolidated', icon: FileText },
                    { key: 'class-split', label: 'Class-split', icon: Layers },
                  ].map((f) => {
                    const active = format === f.key;
                    const Icon = f.icon;
                    return (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => setFormat(f.key)}
                        className={`
                          inline-flex items-center gap-1.5 px-3 h-8 text-[12px] font-semibold rounded-[var(--radius-full)] transition-colors
                          ${active
                            ? 'bg-[var(--color-warm-900)] text-[var(--color-cream-50)] shadow-[var(--shadow-xs)]'
                            : 'text-[var(--color-warm-600)] hover:text-[var(--color-warm-900)]'}
                        `}
                      >
                        <Icon size={13} />
                        {f.label}
                      </button>
                    );
                  })}
                </div>
                {jeReverseDate && (
                  <div className="inline-flex items-center gap-1.5 text-[11px] text-[var(--color-warm-500)]">
                    <Calendar size={13} />
                    Auto-reverse: <span className="font-semibold text-[var(--color-warm-700)]">{jeReverseDate}</span>
                  </div>
                )}
                {closeResult.status && (
                  <Badge tone={closeResult.status === 'finalized' ? 'success' : 'cream'} size="xs">
                    {closeResult.status}
                  </Badge>
                )}
                {closeResult.finalized_at && (
                  <span className="text-[11px] text-[var(--color-warm-500)]">
                    finalized {formatRelative(closeResult.finalized_at)}
                  </span>
                )}
              </div>
              <div className="inline-flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Download size={13} />}
                  onClick={() => onDownload('text')}
                  disabled={!isFromBackend || jeLoading}
                >
                  Download text
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Download size={13} />}
                  onClick={() => onDownload('csv')}
                  disabled={!isFromBackend || jeLoading}
                >
                  Download CSV
                </Button>
              </div>
            </div>
          </Card>

          {endpointMissing && (
            <Card className="mb-4 !p-4 bg-[var(--color-action-50)] border-[var(--color-action-100)]">
              <div className="flex items-start gap-3">
                <AlertTriangle size={16} className="shrink-0 mt-0.5 text-[var(--color-action-600)]" />
                <div className="text-[12px] text-[var(--color-action-700)] leading-relaxed">
                  <strong className="font-semibold">JE backend endpoint not yet deployed.</strong>{' '}
                  Showing a locally computed preview from your close snapshot for review. Download buttons go live once the backend ships <code className="font-mono text-[11px] bg-white/60 px-1 py-0.5 rounded">/api/mec/journal-entry</code>.
                </div>
              </div>
            </Card>
          )}

          {jeError && (
            <Card className="mb-4">
              <div className="flex items-start gap-3 py-1 text-[var(--color-danger-700)]">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <div className="text-[13px]">{jeError}</div>
              </div>
            </Card>
          )}

          {jeLoading && (
            <Card>
              <div className="flex items-center gap-2 justify-center py-8 text-[12px] text-[var(--color-warm-500)]">
                <Loader2 size={14} className="animate-spin" />
                Generating journal entry…
              </div>
            </Card>
          )}

          {!jeLoading && displayJe && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Text view */}
              <Card padded={false}>
                <div className="px-5 pt-5 pb-3 flex items-center justify-between gap-2">
                  <div>
                    <h3 className="text-[14px] font-semibold text-[var(--color-warm-900)]">
                      Formatted text
                    </h3>
                    <p className="text-[11px] text-[var(--color-warm-500)] mt-0.5">
                      Copy and paste into the QuickBooks JE description field.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant={copyState === 'copied' ? 'accent' : 'secondary'}
                    leftIcon={copyState === 'copied' ? <CheckCircle2 size={13} /> : <Copy size={13} />}
                    onClick={onCopy}
                    disabled={!jeText}
                  >
                    {copyState === 'copied' ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <pre className="
                  px-5 pb-5 mx-0 text-[11px] leading-relaxed
                  font-mono text-[var(--color-warm-800)]
                  whitespace-pre-wrap break-words
                  max-h-[480px] overflow-y-auto
                ">
                  {jeText || '(Text format not provided — use the Download text button.)'}
                </pre>
              </Card>

              {/* Line table view */}
              <Card padded={false}>
                <div className="px-5 pt-5 pb-3 flex items-center justify-between gap-2">
                  <div>
                    <h3 className="text-[14px] font-semibold text-[var(--color-warm-900)]">
                      JE lines
                    </h3>
                    <p className="text-[11px] text-[var(--color-warm-500)] mt-0.5">
                      Debit / credit per account{format === 'class-split' ? ' with QBO class.' : '.'}
                    </p>
                  </div>
                  <Badge tone={totals.balanced ? 'success' : 'danger'} size="xs"
                    icon={totals.balanced ? <CheckCircle2 size={11} /> : <AlertTriangle size={11} />}
                  >
                    {totals.balanced ? 'Balanced' : 'Out of balance'}
                  </Badge>
                </div>
                <div className="overflow-x-auto border-t border-[var(--color-warm-100)]">
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="bg-[var(--color-cream-100)] border-b border-[var(--color-warm-100)]">
                        <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[1px] text-[var(--color-warm-500)]">
                          Account
                        </th>
                        {format === 'class-split' && (
                          <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[1px] text-[var(--color-warm-500)]">
                            Class
                          </th>
                        )}
                        <th className="px-3 py-2 text-right text-[10px] font-bold uppercase tracking-[1px] text-[var(--color-warm-500)]">
                          Debit
                        </th>
                        <th className="px-3 py-2 text-right text-[10px] font-bold uppercase tracking-[1px] text-[var(--color-warm-500)]">
                          Credit
                        </th>
                        <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[1px] text-[var(--color-warm-500)]">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayJe.lines?.map((line, i) => {
                        const ld = lineDebitDollars(line);
                        const lc = lineCreditDollars(line);
                        // class info is appended to description in class-split mode by backend
                        return (
                        <tr key={i} className="border-b border-[var(--color-warm-100)] last:border-b-0 hover:bg-[var(--color-cream-100)]/40">
                          <td className="px-3 py-2.5 text-[var(--color-warm-800)] font-medium">
                            {line.account}
                          </td>
                          {format === 'class-split' && (
                            <td className="px-3 py-2.5 text-[var(--color-warm-700)]">
                              {line.class || displayJe.shop_name || '—'}
                            </td>
                          )}
                          <td className="px-3 py-2.5 text-right tabular-nums">
                            {ld > 0 ? (
                              <span className="font-semibold text-[var(--color-warm-900)]">
                                {formatMoney(ld)}
                              </span>
                            ) : (
                              <span className="text-[var(--color-warm-400)]">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums">
                            {lc > 0 ? (
                              <span className="font-semibold text-[var(--color-warm-900)]">
                                {formatMoney(lc)}
                              </span>
                            ) : (
                              <span className="text-[var(--color-warm-400)]">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-[var(--color-warm-600)] text-[11px]">
                            {line.description || ''}
                          </td>
                        </tr>
                        );
                      })}
                      {(!displayJe.lines || displayJe.lines.length === 0) && (
                        <tr>
                          <td colSpan={5} className="px-3 py-10 text-center text-[12px] text-[var(--color-warm-500)] italic">
                            JE has no lines.
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-[var(--color-warm-200)] bg-[var(--color-cream-100)]">
                        <td className={`px-3 py-2.5 font-bold text-[var(--color-warm-900)] ${format === 'class-split' ? 'col-span-2' : ''}`}>
                          Totals
                        </td>
                        {format === 'class-split' && <td />}
                        <td className="px-3 py-2.5 text-right tabular-nums font-bold text-[var(--color-warm-900)]">
                          {formatMoney(totals.debit)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-bold text-[var(--color-warm-900)]">
                          {formatMoney(totals.credit)}
                        </td>
                        <td className={`px-3 py-2.5 text-[11px] ${totals.balanced ? 'text-[var(--color-success-700)]' : 'text-[var(--color-danger-700)]'} font-semibold`}>
                          {totals.balanced ? '✓ Balanced' : `Δ ${formatMoney(Math.abs(totals.debit - totals.credit))}`}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}
